import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Save, X, Trash2, Copy, AlertTriangle } from "lucide-react";
import { useSmsTemplates, SmsTemplate } from "@/hooks/useSmsTemplates";
import { calculateSmsSegments } from "@/utils/smsSegments";

interface SmsTemplateManagerProps {
  templateType: SmsTemplate['template_type'];
  title: string;
  description: string;
}

const PLACEHOLDERS = [
  "#[OrderNumber]", "#[OrderID]", "#[CustomerName]", "#[CustomerPhone]",
  "#[StoreName]", "#[Amount]", "#[PromiseTime]", "#[OfferLink]",
  "#[ClaimLink]", "#[ReviewLink]",
];

const PlaceholderHint = () => (
  <div className="text-xs text-muted-foreground">
    <span className="font-medium text-foreground">Placeholders</span> get swapped for real
    values when the text is sent:
    <span className="mt-1.5 flex flex-wrap gap-1">
      {PLACEHOLDERS.map((p) => (
        <code key={p} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
          {p}
        </code>
      ))}
    </span>
  </div>
);

const SmsTemplateManager = ({ templateType, title, description }: SmsTemplateManagerProps) => {
  const { templates, loading, createTemplate, updateTemplate, deleteTemplate } = useSmsTemplates();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingPromotionalAppend, setEditingPromotionalAppend] = useState("");
  const [newTemplateText, setNewTemplateText] = useState("");
  const [newPromotionalAppend, setNewPromotionalAppend] = useState("");
  const [showNewTemplate, setShowNewTemplate] = useState(false);

  const filteredTemplates = templates.filter(t => t.template_type === templateType);

  const getComplianceColor = (segments: number) => {
    if (segments === 1) return "text-status-success";
    if (segments <= 3) return "text-status-warning";
    return "text-status-danger";
  };

  const handleEdit = (template: SmsTemplate) => {
    setEditingId(template.id);
    setEditingText(template.message_text);
    setEditingPromotionalAppend(template.promotional_append || "");
  };

  const handleSave = async () => {
    if (editingId && editingText.trim()) {
      const success = await updateTemplate(editingId, {
        message_text: editingText.trim(),
        promotional_append: editingPromotionalAppend.trim() || null
      });
      if (success) {
        setEditingId(null);
        setEditingText("");
        setEditingPromotionalAppend("");
      }
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingText("");
    setEditingPromotionalAppend("");
  };

  const handleDelete = async (id: string) => {
    await deleteTemplate(id);
  };

  const handleToggleActive = async (template: SmsTemplate) => {
    await updateTemplate(template.id, { is_active: !template.is_active });
  };

  const handleCreateNew = async () => {
    if (newTemplateText.trim()) {
      const success = await createTemplate({
        template_type: templateType,
        message_text: newTemplateText.trim(),
        promotional_append: newPromotionalAppend.trim() || null,
        is_active: true
      });
      if (success) {
        setNewTemplateText("");
        setNewPromotionalAppend("");
        setShowNewTemplate(false);
      }
    }
  };

  const handleDuplicate = async (template: SmsTemplate) => {
    await createTemplate({
      template_type: templateType,
      message_text: template.message_text + " (Copy)",
      is_active: false
    });
  };

  const StatsDisplay = ({ text }: { text: string }) => {
    const stats = useMemo(() => {
      const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
      const charCount = text.length;
      const segmentData = calculateSmsSegments(text);
      return { wordCount, charCount, ...segmentData };
    }, [text]);

    return (
      <div className="space-y-1 text-xs text-muted-foreground">
        <div className="flex items-center justify-between gap-2">
          <span className="tabular-nums">
            Words: {stats.wordCount} &middot; Characters: {stats.charCount}
          </span>
          <span className={`font-medium tabular-nums ${getComplianceColor(stats.segments)}`}>
            {stats.segments} {stats.segments === 1 ? "segment" : "segments"}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>Encoding: {stats.encoding}</span>
          <span className="tabular-nums">{stats.charsLeft} left in segment</span>
        </div>
        {stats.encoding === 'UCS-2' && (
          <div className="flex items-center text-status-warning">
            <AlertTriangle className="mr-1 h-3 w-3 shrink-0" />
            Special characters cut the characters allowed per segment.
          </div>
        )}
      </div>
    );
  };


  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading templates...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {!showNewTemplate && (
            <Button
              onClick={() => setShowNewTemplate(true)}
              variant="outline"
              className="h-11 shrink-0 sm:h-10"
            >
              <Plus className="h-4 w-4" />
              Add variation
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* New Template Form */}
        {showNewTemplate && (
          <div className="rounded-lg border border-border bg-muted/40 p-4">
            <div className="space-y-3">
              <Label className="text-sm font-medium text-foreground">New template</Label>
              <PlaceholderHint />
              <Textarea
                value={newTemplateText}
                onChange={(e) => setNewTemplateText(e.target.value)}
                placeholder="Enter the base transactional message..."
                className="min-h-[80px] text-base"
              />
              <StatsDisplay text={newTemplateText} />

              {/* Promotional Append for Order Confirmations */}
              {templateType === 'order_confirmation' && (
                <>
                  <Label className="mt-2 text-sm font-medium text-foreground">Promotional append (optional)</Label>
                  <p className="text-xs text-muted-foreground">
                    Added only for <strong className="font-medium text-foreground">subscribed customers</strong>. Leave empty for transactional-only messages.
                  </p>
                  <Textarea
                    value={newPromotionalAppend}
                    onChange={(e) => setNewPromotionalAppend(e.target.value)}
                    placeholder=" Add [Offer Name] for just [Offer Price]! Tap to claim: [ClaimLink]"
                    className="min-h-[60px] text-base"
                  />
                  {newPromotionalAppend && <StatsDisplay text={newTemplateText + newPromotionalAppend} />}
                </>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button
                  onClick={() => {
                    setShowNewTemplate(false);
                    setNewTemplateText("");
                    setNewPromotionalAppend("");
                  }}
                  variant="ghost"
                  className="h-11"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button onClick={handleCreateNew} disabled={!newTemplateText.trim()} className="h-11">
                  <Save className="h-4 w-4" />
                  Save template
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Existing Templates */}
        {filteredTemplates.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No templates yet. Add your first variation above.
          </div>
        ) : (
          filteredTemplates.map((template) => (
            <div key={template.id} className="rounded-lg border border-border p-4">
              <div className="space-y-3">
                {/* Template Header */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={template.is_active}
                      onCheckedChange={() => handleToggleActive(template)}
                      aria-label={template.is_active ? "Deactivate template" : "Activate template"}
                    />
                    <Label className={`text-sm font-medium ${template.is_active ? "text-status-success" : "text-muted-foreground"}`}>
                      {template.is_active ? "Active" : "Inactive"}
                    </Label>
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      onClick={() => handleDuplicate(template)}
                      size="icon"
                      variant="outline"
                      className="h-11 w-11"
                      aria-label="Duplicate template"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    {editingId === template.id ? (
                      <>
                        <Button onClick={handleSave} size="icon" className="h-11 w-11" aria-label="Save changes">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button onClick={handleCancel} size="icon" variant="outline" className="h-11 w-11" aria-label="Cancel editing">
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={() => handleEdit(template)}
                          size="icon"
                          variant="outline"
                          className="h-11 w-11"
                          aria-label="Edit template"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-11 w-11 text-status-danger hover:bg-status-danger/10 hover:text-status-danger"
                              aria-label="Delete template"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this template?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This removes the variation for good. Any messages already sent are not affected.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep it</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(template.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete template
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>

                {/* Template Content */}
                {editingId === template.id ? (
                  <>
                    <PlaceholderHint />
                    <Textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      placeholder="Enter the base transactional message..."
                      className="min-h-[80px] text-base"
                    />
                    <StatsDisplay text={editingText} />

                    {/* Promotional Append for Order Confirmations */}
                    {templateType === 'order_confirmation' && (
                      <>
                        <Label className="mt-2 text-sm font-medium text-foreground">Promotional append (optional)</Label>
                        <p className="text-xs text-muted-foreground">
                          Added only for <strong className="font-medium text-foreground">subscribed customers</strong>. Leave empty for transactional-only messages.
                        </p>
                        <Textarea
                          value={editingPromotionalAppend}
                          onChange={(e) => setEditingPromotionalAppend(e.target.value)}
                          placeholder=" Add [Offer Name] for just [Offer Price]! Tap to claim: [ClaimLink]"
                          className="min-h-[60px] text-base"
                        />
                        {editingPromotionalAppend && <StatsDisplay text={editingText + editingPromotionalAppend} />}
                      </>
                    )}
                  </>
                ) : (
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Base message</Label>
                      <div className="mt-1 rounded-md border border-border bg-muted/50 p-3 text-sm text-foreground">
                        {template.message_text}
                      </div>
                    </div>
                    {templateType === 'order_confirmation' && template.promotional_append && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Promotional append (subscribed customers only)</Label>
                        <div className="mt-1 rounded-md border border-status-info/20 bg-status-info/5 p-3 text-sm text-foreground">
                          {template.promotional_append}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Stats for non-editing templates */}
                {editingId !== template.id && (
                  <StatsDisplay text={template.message_text} />
                )}

                {/* Created/Updated Info */}
                <div className="border-t border-border pt-2 text-xs text-muted-foreground">
                  Created {new Date(template.created_at).toLocaleDateString()} &middot;
                  Updated {new Date(template.updated_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default SmsTemplateManager;
